import React, { useState, forwardRef, useImperativeHandle } from "react";
import SOAPQuestionField from "./SOAPQuestionField";
import VetStartedSOAPSection from "./VetStartedSOAPSection";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
// import { httpService } from "../../services/httpService";
// import { toast } from "react-toastify";

// Field Label Component
const FieldLabel = ({ children }) => (
  <span className="text-[11px] font-semibold text-slate-600 w-28 shrink-0">
    {children}
  </span>
);

// Chip Group for multi-select
const ChipGroup = ({ value, options, onChange, multi = false, className = "" }) => {
  const selected = multi ? (Array.isArray(value) ? value : []) : value;

  const toggle = (opt) => {
    if (multi) {
      const arr = Array.isArray(selected) ? selected : [];
      onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    } else {
      onChange(selected === opt ? "" : opt);
    }
  };

  const isActive = (opt) => (multi ? selected.includes(opt) : selected === opt);

  return (
    <div className={`flex flex-wrap gap-1.5 flex-1 ${className}`}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg border transition-all ${
            isActive(opt)
              ? "bg-violet-600 border-violet-600 text-white"
              : "bg-white border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
};

// Select Dropdown
const SelectDropdown = ({ value, options, onChange, placeholder, className = "" }) => (
  <div className={`relative flex-1 max-w-[200px] ${className}`}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none text-[12px] text-slate-700 border border-slate-200 rounded-xl px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent bg-white"
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
  </div>
);

// Vomiting Detail Sub-form
const VomitingDetail = ({ value = {}, onChange }) => {
  const patch = (k, v) => onChange({ ...value, [k]: v });
  if (!value.present) return null;
  
  return (
    <div className="mt-2 ml-32 pl-3 border-l-2 border-violet-100 space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-slate-500 w-16">Frequency</span>
          <SelectDropdown
            value={value.frequency || ""}
            onChange={(v) => patch("frequency", v)}
            options={["Once", "2–3×/day", "4–5×/day", "> 5×/day"]}
            placeholder="Frequency"
            className="max-w-[160px]"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-slate-500 w-14">Duration</span>
          <SelectDropdown
            value={value.duration || ""}
            onChange={(v) => patch("duration", v)}
            options={["< 24hrs", "1–3 days", "4–7 days", "> 1 week"]}
            placeholder="Duration"
            className="max-w-[160px]"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold text-slate-500 w-16">Colour</span>
        <ChipGroup
          value={value.colour || ""}
          options={["Yellow/bile", "White/foam", "Brown", "Red/blood", "Undigested food", "Other"]}
          onChange={(v) => patch("colour", v)}
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold text-slate-500 w-16">Blood present?</span>
        <ChipGroup
          value={value.blood || ""}
          options={["Yes", "No"]}
          onChange={(v) => patch("blood", v)}
        />
      </div>
    </div>
  );
};

// History Tab Component - Two Column Layout
const HistoryTab = ({ data, onChange }) => {
  const patch = (key, val) => onChange({ ...data, [key]: val });

  const MEDICAL_CONDITIONS = [
    "Diabetes", "Skin allergies", "Epilepsy", "Ear infection",
    "Heart disease", "Kidney disease", "Arthritis", "Hypothyroidism",
    "Hyperthyroidism", "Cushing's disease", "Addison's disease", "Other",
  ];

  const WATER_INTAKE_OPTIONS = [
    "Normal", "Increased (polydipsia)", "Decreased", "Unknown",
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-6">
        <div className="flex items-center gap-3">
          <FieldLabel>Diet:</FieldLabel>
          <ChipGroup
            value={data.diet || ""}
            options={["Normal", "Reduced", "Increased", "Not Eating"]}
            onChange={(v) => patch("diet", v)}
          />
        </div>
        <div className="flex items-center gap-3">
          <FieldLabel>Activity Level:</FieldLabel>
          <ChipGroup
            value={data.activityLevel || ""}
            options={["Normal", "Reduced", "Lethargic", "Hyperactive"]}
            onChange={(v) => patch("activityLevel", v)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="flex items-center gap-3">
          <FieldLabel>Water Intake:</FieldLabel>
          <SelectDropdown
            value={data.waterIntake || ""}
            options={WATER_INTAKE_OPTIONS}
            onChange={(v) => patch("waterIntake", v)}
            placeholder="Select…"
          />
        </div>
        <div className="flex items-center gap-3">
          <FieldLabel>Defecation:</FieldLabel>
          <ChipGroup
            value={data.defecation || ""}
            options={["Normal", "Diarrhea", "Constipation", "Blood in stool"]}
            onChange={(v) => patch("defecation", v)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="flex items-center gap-3">
          <FieldLabel>Vaccination Status:</FieldLabel>
          <ChipGroup
            value={data.vaccinationStatus || ""}
            options={["Up to date", "Overdue", "Unknown"]}
            onChange={(v) => patch("vaccinationStatus", v)}
          />
        </div>
        <div className="flex items-center gap-3">
          <FieldLabel>Vomiting:</FieldLabel>
          <ChipGroup
            value={data.vomiting?.present || ""}
            options={["Yes", "No"]}
            onChange={(v) => patch("vomiting", { ...data.vomiting, present: v })}
          />
        </div>
      </div>

      {data.vomiting?.present === "Yes" && (
        <div className="col-span-2">
          <VomitingDetail
            value={data.vomiting || {}}
            onChange={(v) => patch("vomiting", v)}
          />
        </div>
      )}

      <div className="pt-2">
        <div className="flex items-center gap-3 mb-2">
          <FieldLabel>Medical Conditions:</FieldLabel>
          <span className="text-[10px] text-slate-400">(Select multiple)</span>
        </div>
        <div className="ml-28">
          <ChipGroup
            value={data.medicalConditions || []}
            options={MEDICAL_CONDITIONS}
            onChange={(v) => patch("medicalConditions", v)}
            multi
          />
        </div>
      </div>
    </div>
  );
};

// ─── Main Subjective Component (Self-contained) ─────────────────────────────

const Subjective = forwardRef(({
  defaultOpen = true,
  isExpanded = true,
  onToggle,
  onDirtyChange,
  initialData, // NEW — the `subjective` slice of the fetched SOAP note, or undefined
}, ref) => {
  const [activeSubTab, setActiveSubTab] = useState("observations");

  const [data, setData] = useState(() => ({
    structuredEntries: [],
    diet: "",
    activityLevel: "",
    waterIntake: "",
    defecation: "",
    vaccinationStatus: "",
    vomiting: { present: "", frequency: "", duration: "", colour: "", blood: "" },
    medicalConditions: [],
    ...(initialData || {}),
  }));

  useImperativeHandle(ref, () => ({
    getData: () => data,
  }));

  const subTabs = [
    { id: "observations", label: "Internal Notes / Observations" },
    { id: "history", label: "History" },
  ];

  const answeredCount = () => {
    if (activeSubTab === "observations") {
      return data.structuredEntries?.filter(e => e.answer).length || 0;
    } else {
      return [
        data.diet, data.activityLevel, data.waterIntake, data.defecation,
        data.vaccinationStatus, data.vomiting?.present, data.medicalConditions?.length
      ].filter(Boolean).length;
    }
  };

  const handleDataChange = (newData) => {
    setData(newData);
    onDirtyChange?.(true);
  };

  return (
    <VetStartedSOAPSection
      id="soap-section-S"
      letter="S"
      title="Subjective"
      subtitle="Owner-reported history & presenting complaints"
      accentBg="#f3f0ff"
      accentColor="#6d28d9"
      badge={answeredCount() > 0 ? `${answeredCount()} answered` : null}
      defaultOpen={defaultOpen}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-nav-tab="true"
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-3 py-2 text-[11px] font-semibold rounded-t-lg transition-all ${
              activeSubTab === tab.id
                ? "text-violet-600 border-b-2 border-violet-600 bg-violet-50/50"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === "observations" && (
        <div>
          <SOAPQuestionField
            section="subjective"
            label="Internal Notes / Observations"
            structuredEntries={data.structuredEntries || []}
            onEntriesChange={(entries) => handleDataChange({ ...data, structuredEntries: entries })}
            textareaRows={5}
          />
        </div>
      )}

      {activeSubTab === "history" && (
        <HistoryTab
          data={data}
          onChange={handleDataChange}
        />
      )}
    </VetStartedSOAPSection>
  );
});

Subjective.displayName = "Subjective";

export default Subjective;